import React, { useState, useContext, useEffect } from "react";
import { UserContext } from "../../contexts/UserContext";
import { Button } from "../../components/ui/button";
import { Separator } from "../../components/ui/separator";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Eye, Trash2, AlertTriangle, AlertOctagon, ArrowUp, ArrowDown, Clock } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_URL.replace(/\/$/, "") + "/api";

const ECCWarningActionsTab = ({ activeSubTab }) => {
  const { user } = useContext(UserContext);

  // Warning Actions States
  const [showWarningDetails, setShowWarningDetails] = useState(false);
  const [showNewWarningModal, setShowNewWarningModal] = useState(false);
  const [selectedWarning, setSelectedWarning] = useState(null);
  const [newWarningData, setNewWarningData] = useState({
    userId: "",
    userName: "",
    type: "warning",
    reason: "",
  });

  // Warning Actions Data
  const [warningActions, setWarningActions] = useState([]);
  const [penaltyTypes, setPenaltyTypes] = useState([]);
  const [loadingPenaltyTypes, setLoadingPenaltyTypes] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  console.log("nomeneeslist", usersList)
  // Search and Filter States
  const [activeSearch, setActiveSearch] = useState("");
  const [expiredSearch, setExpiredSearch] = useState("");
  const [selectedWarningType, setSelectedWarningType] = useState("");

  // Paginacja
  const [currentPage, setCurrentPage] = useState({ active: 1, expired: 1 });
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Sortowanie
  const [sortConfig, setSortConfig] = useState({
    active: { key: "user_name", direction: "asc" },
    expired: { key: "user_name", direction: "asc" },
  });

  // Określanie widoczności kart
  const visibleCards = {
    active: activeSubTab === 'warnings-active',
    expired: activeSubTab === 'warnings-expired',
  };

  // Pobieranie danych z backendu
  const fetchPenalties = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/penalty/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      if (!response.ok) throw new Error("Błąd pobierania danych");
      const data = await response.json();
      setWarningActions(data);
    } catch (error) {
      console.error("❌ Błąd pobierania warningów:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPenaltyTypes = async () => {
    try {
      setLoadingPenaltyTypes(true);
      const response = await fetch(`${API_BASE_URL}/penalty-types/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      if (!response.ok) throw new Error("Błąd pobierania typów kar");
      const data = await response.json();
      setPenaltyTypes(data);
    } catch (error) {
      console.error("❌ Błąd pobierania penalty-types:", error);
      setError(error.message);
    } finally {
      setLoadingPenaltyTypes(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await fetch(`${API_BASE_URL}/nominees-list/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      if (!response.ok) throw new Error("Błąd pobierania listy użytkowników");
      const data = await response.json();
      setUsersList(data);
    } catch (error) {
      console.error("❌ Błąd pobierania użytkowników:", error);
      setError(error.message);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchPenalties();
    fetchPenaltyTypes();   
  }, []);


  useEffect(() => {
    if (showNewWarningModal) {
      fetchUsers();
    }
  }, [showNewWarningModal]);

  const handleAddWarning = async () => {
    if (!newWarningData.userId || !newWarningData.type || !newWarningData.reason.trim()) {
      console.error("⚠️ Wszystkie pola muszą być uzupełnione!");
      return;
    }

    const currentDate = new Date();
    const expiryDate = new Date();
    if (newWarningData.type === "red") {
      expiryDate.setMonth(expiryDate.getMonth() + 6);
    } else if (newWarningData.type === "yellow") {
      expiryDate.setMonth(expiryDate.getMonth() + 3);
    } else {
      expiryDate.setMonth(expiryDate.getMonth() + 1);
    }

    const typeMapping = {
      warning: "Warning",
      yellow: "Yellow Card",
      red: "Red Card",
    };

    const selectedCardType = penaltyTypes.find(
      (type) => type.name === typeMapping[newWarningData.type]
    );

    if (!selectedCardType) {
      console.error("❌ Nie znaleziono ID dla wybranego typu ostrzeżenia!");
      return;
    }

    const warningData = {
      user: newWarningData.userId,
      reason: newWarningData.reason,
      date_expiration: expiryDate.toISOString().split("T")[0],
      card_type: selectedCardType.id,
    };

    try {
      const response = await fetch(`${API_BASE_URL}/penalty/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        body: JSON.stringify(warningData),
      });

      const responseData = await response.json();
      if (!response.ok) {
        throw new Error(`❌ Błąd API: ${response.status} - ${responseData.message || JSON.stringify(responseData)}`);
      }

      setWarningActions([...warningActions, responseData]);
      setShowNewWarningModal(false);
      setNewWarningData({
        userId: "",
        userName: "",
        type: "warning",
        reason: "",
      });
    } catch (error) {
      console.error("❌ Wystąpił błąd:", error);
    }
  };

  const handleDeleteWarning = async (warningId) => {
    const confirmDelete = window.confirm("Czy na pewno chcesz usunąć to ostrzeżenie?");
    if (!confirmDelete) return;

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const formattedYesterday = yesterday.toISOString().split("T")[0];

    try {
      const response = await fetch(`${API_BASE_URL}/penalty/${warningId}/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        body: JSON.stringify({ date_expiration: formattedYesterday }),
      });

      if (!response.ok) throw new Error("Nie udało się usunąć ostrzeżenia.");
      fetchPenalties();
    } catch (error) {
      console.error("❌ Błąd usuwania ostrzeżenia:", error);
      alert("Wystąpił problem podczas usuwania ostrzeżenia.");
    }
  };

  const formatDate = (date) => new Date(date).toLocaleDateString("pl-PL");
  const formatDateTime = (dateTime) =>
    new Date(dateTime).toLocaleString("pl-PL", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

  const getCardTypeVariant = (cardType) => {
    const variants = {
      3: "destructive", // Warning
      2: "destructive1", // Red Card
      1: "warning", // Yellow Card
    };
    return variants[cardType] || "default";
  };

  const activeWarnings = warningActions.filter((warning) => warning.active);
  const expiredWarnings = warningActions.filter((warning) => !warning.active);

  const getPageTitle = () => {
    return visibleCards.active
      ? { title: "Active Warnings", Icon: AlertTriangle }
      : { title: "Expired Warnings", Icon: Clock };
  };

  const filterWarnings = (warnings, searchTerm, warningType) => {
    let filtered = warnings;
    if (searchTerm) {
      const lowerCaseSearch = searchTerm.toLowerCase();
      filtered = filtered.filter((warning) =>
        (
          (warning.user_name && warning.user_name.toLowerCase().includes(lowerCaseSearch)) ||
          (warning.punisher_name && warning.punisher_name.toLowerCase().includes(lowerCaseSearch)) ||
          formatDate(warning.date_issued).includes(lowerCaseSearch) ||
          formatDate(warning.date_expiration).includes(lowerCaseSearch) ||
          formatDateTime(warning.date_issued).includes(lowerCaseSearch) ||
          formatDateTime(warning.date_expiration).includes(lowerCaseSearch)
        )
      );
    }
    if (warningType) {
      filtered = filtered.filter((warning) => warning.card_type_data?.name === warningType);
    }
    return filtered;
  };

  const sortWarnings = (warnings, view) => {
    const { key, direction } = sortConfig[view];
    const sorted = [...warnings];

    const warningTypeOrder = { "Warning": 1, "Yellow Card": 2, "Red Card": 3 };

    sorted.sort((a, b) => {
      if (key === "user_name" || key === "punisher_name") {
        const aLastName = (key === "user_name" ? a.user_name : a.punisher_name || "").split(" ").pop();
        const bLastName = (key === "user_name" ? b.user_name : b.punisher_name || "").split(" ").pop();
        return direction === "asc"
          ? aLastName.localeCompare(bLastName)
          : bLastName.localeCompare(aLastName);
      } else if (key === "date_issued" || key === "date_expiration") {
        const aDate = new Date(a[key]);
        const bDate = new Date(b[key]);
        return direction === "asc" ? aDate - bDate : bDate - aDate;
      } else if (key === "card_type_data.name") {
        const aValue = warningTypeOrder[a.card_type_data?.name] || 0;
        const bValue = warningTypeOrder[b.card_type_data?.name] || 0;
        return direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });

    return sorted;
  };

  const filteredActiveWarnings = sortWarnings(
    filterWarnings(activeWarnings, activeSearch, selectedWarningType),
    "active"
  );
  const filteredExpiredWarnings = sortWarnings(
    filterWarnings(expiredWarnings, expiredSearch, selectedWarningType),
    "expired"
  );

  // Reset to first page when items per page changes or when filtered results change
  useEffect(() => {
    const activeData = visibleCards.active ? filteredActiveWarnings : filteredExpiredWarnings;
    const calculatedTotalPages = Math.ceil(activeData.length / itemsPerPage) || 1;
    const currentKey = visibleCards.active ? 'active' : 'expired';
    if (currentPage[currentKey] > calculatedTotalPages && calculatedTotalPages > 0) {
      handlePageChange(currentKey, 1);
    }
  }, [itemsPerPage, filteredActiveWarnings.length, filteredExpiredWarnings.length, visibleCards.active, visibleCards.expired, currentPage, filteredActiveWarnings, filteredExpiredWarnings]);

  const paginate = (data, page, itemsPerPage) => {
    const startIndex = (page - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  const getTotalPages = (data) => Math.ceil(data.length / itemsPerPage);

  const handlePageChange = (view, page) => {
    setCurrentPage((prev) => ({ ...prev, [view]: page }));
  };

  const handleSort = (view, key) => {
    setSortConfig((prev) => {
      const currentDirection = prev[view].key === key && prev[view].direction === "asc" ? "desc" : "asc";
      return { ...prev, [view]: { key, direction: currentDirection } };
    });
    handlePageChange(view, 1); // Reset strony po sortowaniu
  };

  const renderSortIndicator = (view, columnKey) => {
    const { key, direction } = sortConfig[view];
    if (key !== columnKey) return null;
    return direction === "asc" ? (
      <ArrowDown className="h-3 w-3 ml-1" strokeWidth={1.5} />
    ) : (
      <ArrowUp className="h-3 w-3 ml-1" strokeWidth={1.5} />
    );
  };

  const renderTable = (warnings, view) => {
    const { title, Icon } = getPageTitle();
    const totalPages = getTotalPages(warnings);
    const current = currentPage[view];
    const paginatedWarnings = paginate(warnings, current, itemsPerPage);

    const getVisiblePages = () => {
      const maxVisiblePages = 5; // Bieżąca strona + 2 do przodu + 2 do tyłu
      const pages = [];
      const startPage = Math.max(1, current - 2);
      const endPage = Math.min(totalPages, current + 2);

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      return pages;
    };

    return (
      <div className="h-screen flex flex-col">
        {/* Header Section */}
        <div className="flex-shrink-0 p-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <h1 className="flex items-center text-2xl font-bold text-graphite-900">
              <Icon className="h-6 w-6 mr-3 text-brand-500" />
              {title}
            </h1>
            <div className="flex space-x-2 mt-4 md:mt-0">
              <Button
                onClick={() => setShowNewWarningModal(true)}
                className="px-4 py-2 rounded-full flex items-center bg-brand-500 text-white hover:bg-brand-600"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Issue Warning
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 px-4 pb-4 overflow-hidden">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-steel-200 h-full flex flex-col">
            <div className="p-4 flex-shrink-0 border-b-2 border-brand-500 bg-steel-50">
              <div className="flex flex-col md:flex-row items-center gap-4">
                <div className="relative flex-1 w-full">
                  <input
                    placeholder="Search by user, issuer, date..."
                    className="w-full pl-10 pr-4 py-2.5 border border-steel-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-graphite-900 placeholder:text-graphite-400"
                    type="text"
                    value={view === "active" ? activeSearch : expiredSearch}
                    onChange={(e) => {
                      if (view === "active") setActiveSearch(e.target.value);
                      else setExpiredSearch(e.target.value);
                      handlePageChange(view, 1);
                    }}
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-search absolute left-3 top-1/2 transform -translate-y-1/2 text-graphite-400 h-5 w-5"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>
                </div>
                <select
                  value={selectedWarningType}
                  onChange={(e) => {
                    setSelectedWarningType(e.target.value);
                    handlePageChange(view, 1);
                  }}
                  className="border border-steel-300 rounded-xl px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 min-w-[180px]"
                >
                  <option value="">All warning types</option>
                  {penaltyTypes.map((type) => (
                    <option key={type.id} value={type.name}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Table container */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex-1 overflow-y-auto">
                <table className="min-w-full divide-y divide-steel-200">
                  <thead className="bg-steel-100 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">
                        <button
                          className="flex items-center gap-1 hover:text-brand-500 transition-colors"
                          onClick={() => handleSort(view, "user_name")}
                        >
                          User
                          {renderSortIndicator(view, "user_name")}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">
                        <button
                          className="flex items-center gap-1 hover:text-brand-500 transition-colors"
                          onClick={() => handleSort(view, "card_type_data.name")}
                        >
                          Warning Type
                          {renderSortIndicator(view, "card_type_data.name")}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">
                        <button
                          className="flex items-center gap-1 hover:text-brand-500 transition-colors"
                          onClick={() => handleSort(view, "date_issued")}
                        >
                          Issue Date
                          {renderSortIndicator(view, "date_issued")}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">
                        <button
                          className="flex items-center gap-1 hover:text-brand-500 transition-colors"
                          onClick={() => handleSort(view, "punisher_name")}
                        >
                          Issuer
                          {renderSortIndicator(view, "punisher_name")}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">
                        <button
                          className="flex items-center gap-1 hover:text-brand-500 transition-colors"
                          onClick={() => handleSort(view, "date_expiration")}
                        >
                          Expiry Date
                          {renderSortIndicator(view, "date_expiration")}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-steel-100">
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-graphite-500">
                          <div className="flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-500"></div>
                            Loading {title.toLowerCase()}...
                          </div>
                        </td>
                      </tr>
                    ) : paginatedWarnings.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-graphite-500">
                          No {title.toLowerCase()} available.
                        </td>
                      </tr>
                    ) : (
                      paginatedWarnings.map((warning) => (
                        <tr
                          key={warning.id}
                          className="hover:bg-brand-50/30 transition-colors cursor-pointer"
                          onDoubleClick={() => {
                            setSelectedWarning(warning);
                            setShowWarningDetails(true);
                          }}
                        >
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-graphite-900">{warning.user_name}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <Badge variant={getCardTypeVariant(warning.card_type)}>
                              {warning.card_type_data?.name || "Unknown"}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-graphite-600">{formatDate(warning.date_issued)}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-graphite-600">{warning.punisher_name}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-graphite-600">{formatDate(warning.date_expiration)}</td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <div className="flex gap-2">
                              <button
                                className="p-1.5 bg-steel-100 text-graphite-700 rounded-full hover:bg-steel-200 transition-colors"
                                aria-label="View"
                                onClick={() => {
                                  setSelectedWarning(warning);
                                  setShowWarningDetails(true);
                                }}
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              {view === "active" && (
                                <button
                                  onClick={() => handleDeleteWarning(warning.id)}
                                  className="p-1.5 bg-red-50 text-red-600 rounded-full hover:bg-red-100 transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-4 border-t border-steel-200 bg-steel-50">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-graphite-600">
                    Showing <span className="font-medium text-graphite-900">{(current - 1) * itemsPerPage + 1}-{Math.min(current * itemsPerPage, warnings.length)}</span> of <span className="font-medium text-graphite-900">{warnings.length}</span> items
                  </div>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      handlePageChange(view, 1);
                    }}
                    className="text-sm px-4 py-2 border border-steel-300 rounded-xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 min-w-[140px]"
                  >
                    <option value={5}>5 per page</option>
                    <option value={10}>10 per page</option>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  {current > 1 && (
                    <button
                      className="px-4 py-2 border border-steel-300 text-graphite-700 rounded-xl bg-white hover:bg-steel-50 hover:border-brand-300 flex items-center justify-center transition-colors"
                      onClick={() => handlePageChange(view, current - 1)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-chevron-left h-4 w-4 mr-1"
                      >
                        <path d="m15 18-6-6 6-6" />
                      </svg>
                      Previous
                    </button>
                  )}
                  {getVisiblePages().map((page) => (
                    <button
                      key={page}
                      className={`w-10 h-10 border ${
                        page === current
                          ? "border-brand-500 bg-brand-500 text-white font-medium shadow-sm"
                          : "border-steel-300 text-graphite-700 bg-white hover:bg-steel-50 hover:border-brand-300"
                      } rounded-xl flex items-center justify-center transition-colors`}
                      onClick={() => handlePageChange(view, page)}
                    >
                      {page}
                    </button>
                  ))}
                  {current < totalPages && (
                    <button
                      className="px-4 py-2 border border-steel-300 text-graphite-700 rounded-xl bg-white hover:bg-steel-50 hover:border-brand-300 flex items-center justify-center transition-colors"
                      onClick={() => handlePageChange(view, current + 1)}
                    >
                      Next
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-chevron-right h-4 w-4 ml-1"
                      >
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen flex flex-col">
      {visibleCards.active && renderTable(filteredActiveWarnings, "active")}
      {visibleCards.expired && renderTable(filteredExpiredWarnings, "expired")}

      {/* Warning Details Modal */}
      <Dialog open={showWarningDetails} onOpenChange={setShowWarningDetails}>
        <DialogContent className="max-w-xl rounded-2xl p-0 overflow-hidden">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-brand-50 to-white px-6 py-4 border-b border-brand-200">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-graphite-900 flex items-center gap-2">
                <div className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4 text-brand-600" />
                </div>
                Warning Details
              </DialogTitle>
            </DialogHeader>
          </div>

          {selectedWarning ? (
            <div className="p-6 space-y-5">
              {/* Info Cards Grid */}
              <div className="grid grid-cols-2 gap-3">
                {/* User Card */}
                <div className="bg-steel-50 px-3 py-2.5 rounded-lg border border-steel-100">
                  <p className="text-[11px] text-graphite-500 uppercase tracking-wide">User</p>
                  <p className="font-semibold text-graphite-900 text-sm">{selectedWarning.user_name}</p>
                </div>

                {/* Warning Type Card */}
                <div className="bg-steel-50 px-3 py-2.5 rounded-lg border border-steel-100">
                  <p className="text-[11px] text-graphite-500 uppercase tracking-wide">Warning Type</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {selectedWarning?.card_type_data?.name === "Red Card" && (
                      <div className="w-4 h-4 bg-red-600 rounded-sm"></div>
                    )}
                    {selectedWarning?.card_type_data?.name === "Yellow Card" && (
                      <div className="w-4 h-4 bg-yellow-500 rounded-sm"></div>
                    )}
                    {selectedWarning?.card_type_data?.name === "Warning" && (
                      <div className="w-4 h-4 bg-brand-500 rounded-sm flex items-center justify-center">
                        <span className="text-white text-[10px]">!</span>
                      </div>
                    )}
                    <span className="font-semibold text-graphite-900 text-sm">{selectedWarning?.card_type_data?.name || "Unknown"}</span>
                  </div>
                </div>

                {/* Issuer Card */}
                <div className="bg-steel-50 px-3 py-2.5 rounded-lg border border-steel-100">
                  <p className="text-[11px] text-graphite-500 uppercase tracking-wide">Issued By</p>
                  <p className="font-semibold text-graphite-900 text-sm">{selectedWarning.punisher_name}</p>
                </div>

                {/* Issue Date Card */}
                <div className="bg-steel-50 px-3 py-2.5 rounded-lg border border-steel-100">
                  <p className="text-[11px] text-graphite-500 uppercase tracking-wide">Issue Date</p>
                  <p className="font-semibold text-graphite-900 text-sm">{formatDateTime(selectedWarning.date_issued)}</p>
                </div>

                {/* Revoker Card (conditional) */}
                {selectedWarning?.revoker && (
                  <div className="bg-steel-50 px-3 py-2.5 rounded-lg border border-steel-100">
                    <p className="text-[11px] text-graphite-500 uppercase tracking-wide">Revoker</p>
                    <p className="font-semibold text-graphite-900 text-sm">{selectedWarning.revoker_name}</p>
                  </div>
                )}

                {/* Expiry Date Card */}
                <div className="bg-steel-50 px-3 py-2.5 rounded-lg border border-steel-100">
                  <p className="text-[11px] text-graphite-500 uppercase tracking-wide">Expiry Date</p>
                  <p className="font-semibold text-graphite-900 text-sm">{formatDate(selectedWarning.date_expiration)}</p>
                </div>
              </div>

              {/* Reason Section */}
              <div className="bg-steel-50 px-4 py-3 rounded-lg border border-steel-100">
                <p className="text-[11px] text-graphite-500 uppercase tracking-wide mb-1">Reason</p>
                <p className="text-graphite-700 text-sm">{selectedWarning.reason}</p>
              </div>

              {/* Footer with buttons */}
              <div className="flex justify-end gap-2 pt-2 border-t border-steel-200">
                <Button
                  variant="outline"
                  className="px-6 py-2 rounded-full border-steel-300 text-graphite-900 hover:bg-steel-50"
                  onClick={() => setShowWarningDetails(false)}
                >
                  Close
                </Button>
                {selectedWarning.active && (
                  <Button
                    className="px-6 py-2 rounded-full bg-red-600 text-white hover:bg-red-700"
                    onClick={() => {
                      handleDeleteWarning(selectedWarning.id);
                      setShowWarningDetails(false);
                    }}
                  >
                    Delete Warning
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="p-6">
              <p className="text-graphite-500 text-sm">Nie znaleziono ostrzeżenia.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Warning Modal */}
      <Dialog open={showNewWarningModal} onOpenChange={setShowNewWarningModal}>
        <DialogContent className="max-w-md rounded-2xl p-0 overflow-hidden">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-brand-50 to-white px-6 py-4 border-b border-brand-200">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-graphite-900 flex items-center gap-2">
                <div className="w-8 h-8 bg-brand-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4 text-brand-600" />
                </div>
                Issue New Warning
              </DialogTitle>
            </DialogHeader>
          </div>

          <div className="p-6 space-y-5">
            {/* User Selection */}
            <div className="space-y-2">
              <label className="text-[11px] font-medium text-graphite-500 uppercase tracking-wide">User</label>
              <select
                value={newWarningData.userId}
                onChange={(e) => {
                  const selectedUser = usersList.find((u) => u.id === parseInt(e.target.value));
                  setNewWarningData((prev) => ({
                    ...prev,
                    userId: selectedUser?.id || "",
                    userName: selectedUser?.name || "",
                  }));
                }}
                className="w-full rounded-lg border border-steel-300 p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                required
              >
                <option value="">Select a user</option>
                {usersList.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.id})
                  </option>
                ))}
              </select>
              {loadingUsers && <p className="text-xs text-graphite-500">Ładowanie użytkowników...</p>}
              {!loadingUsers && usersList.length === 0 && (
                <p className="text-xs text-red-500">Brak dostępnych użytkowników.</p>
              )}
            </div>

            {/* Warning Type Selection */}
            <div className="space-y-2">
              <label className="text-[11px] font-medium text-graphite-500 uppercase tracking-wide">Warning Type</label>
              <div className="grid grid-cols-3 gap-2">
                <label className={`flex flex-col items-center p-3 rounded-lg border cursor-pointer transition-all ${
                  newWarningData.type === "warning"
                    ? "border-brand-500 bg-brand-50"
                    : "border-steel-200 hover:border-steel-300"
                }`}>
                  <input
                    type="radio"
                    value="warning"
                    checked={newWarningData.type === "warning"}
                    onChange={(e) => setNewWarningData((prev) => ({ ...prev, type: e.target.value }))}
                    className="sr-only"
                  />
                  <div className="w-6 h-6 bg-brand-500 rounded flex items-center justify-center mb-1">
                    <span className="text-white text-xs font-bold">!</span>
                  </div>
                  <span className="text-xs font-medium text-graphite-700">Warning</span>
                  <span className="text-[10px] text-graphite-500">1 month</span>
                </label>

                <label className={`flex flex-col items-center p-3 rounded-lg border cursor-pointer transition-all ${
                  newWarningData.type === "yellow"
                    ? "border-yellow-500 bg-yellow-50"
                    : "border-steel-200 hover:border-steel-300"
                }`}>
                  <input
                    type="radio"
                    value="yellow"
                    checked={newWarningData.type === "yellow"}
                    onChange={(e) => setNewWarningData((prev) => ({ ...prev, type: e.target.value }))}
                    className="sr-only"
                  />
                  <div className="w-6 h-6 bg-yellow-500 rounded mb-1"></div>
                  <span className="text-xs font-medium text-graphite-700">Yellow Card</span>
                  <span className="text-[10px] text-graphite-500">3 months</span>
                </label>

                <label className={`flex flex-col items-center p-3 rounded-lg border cursor-pointer transition-all ${
                  newWarningData.type === "red"
                    ? "border-red-500 bg-red-50"
                    : "border-steel-200 hover:border-steel-300"
                }`}>
                  <input
                    type="radio"
                    value="red"
                    checked={newWarningData.type === "red"}
                    onChange={(e) => setNewWarningData((prev) => ({ ...prev, type: e.target.value }))}
                    className="sr-only"
                  />
                  <div className="w-6 h-6 bg-red-600 rounded mb-1"></div>
                  <span className="text-xs font-medium text-red-600">Red Card</span>
                  <span className="text-[10px] text-graphite-500">6 months</span>
                </label>
              </div>
            </div>

            {/* Reason Textarea */}
            <div className="space-y-2">
              <label className="text-[11px] font-medium text-graphite-500 uppercase tracking-wide">Reason</label>
              <Textarea
                value={newWarningData.reason}
                onChange={(e) => setNewWarningData((prev) => ({ ...prev, reason: e.target.value }))}
                placeholder="Enter the reason for issuing this warning..."
                rows={4}
                className="rounded-lg border-steel-300 text-sm focus:ring-brand-500 focus:border-brand-500"
                required
              />
            </div>

            {/* Red Card Warning Alert */}
            {newWarningData.type === "red" && (
              <div className="rounded-lg bg-red-50 p-4 border border-red-200">
                <div className="flex items-start">
                  <AlertOctagon className="h-5 w-5 text-red-500 flex-shrink-0" />
                  <div className="ml-3">
                    <h3 className="text-sm font-semibold text-red-800">Red Card Warning</h3>
                    <div className="mt-1 text-xs text-red-700">
                      <p>Issuing a red card will automatically:</p>
                      <ul className="list-disc list-inside mt-1 space-y-0.5">
                        <li>Suspend all active nominations</li>
                        <li>Suspend all active work permits</li>
                        <li>Block new submissions for 6 months</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-steel-200 flex justify-end gap-2">
            <Button
              variant="outline"
              className="px-6 py-2 rounded-full border-steel-300 text-graphite-900 hover:bg-steel-50"
              onClick={() => setShowNewWarningModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              className="px-6 py-2 rounded-full bg-brand-500 text-white hover:bg-brand-600"
              onClick={handleAddWarning}
              disabled={!newWarningData.userId || !newWarningData.type || !newWarningData.reason.trim() || loadingUsers}
            >
              Issue Warning
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ECCWarningActionsTab;